/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    ICreateActionContext,
    IRequestContext
} from '@msdyn365-commerce/core';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getAttributeValuesAsync, getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    ArrayExtensions,
    buildCacheKey,
    generateImageUrl,
    getProductUrlSync,
    getSelectedProductIdFromActionInput,
    QueryResultSettingsProxy,
    removeDomainQspFromUrl
} from '@msdyn365-commerce-modules/retail-actions';

import { IPageSummaryData } from '@msdyn365-commerce-modules/page-summary';
import { IDobbiesProductPageSummaryConfig } from './dobbies-product-page-summary.props.autogenerated';

/**
 * Strips HTML tags and decodes HTML entities from a string, returning plain text.
 * Limits the result to the first sentence (up to the first full stop).
 */
const stripHtmlTags = (html: string | undefined): string | undefined => {
    if (!html) {
        return html;
    }
    const plainText = html
        .replace(/<[^>]*>/g, '') // Remove all HTML tags including attributes
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/&apos;/gi, "'")
        .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code, 10)))
        .replace(/&#x([0-9a-f]+);/gi, (_, code) => String.fromCharCode(parseInt(code, 16)))
        .replace(/&\w+;/g, '') // Remove any remaining HTML entities
        .replace(/\s+/g, ' ')
        .trim();

    // Limit to first sentence (up to first full stop)
    const firstSentenceMatch = plainText.match(/^[^.]*\./);
    return firstSentenceMatch ? firstSentenceMatch[0].trim() : plainText;
};

/**
 * Product Page Summary Input.
 */
export class ProductPageSummaryInput implements IActionInput {
    public apiSettings: ICommerceApiSettings;

    public config: IDobbiesProductPageSummaryConfig;

    public productId: number;

    public catalogId?: number;

    public constructor(
        config: IDobbiesProductPageSummaryConfig,
        apiSettings: ICommerceApiSettings,
        productId: number,
        requestContext?: IRequestContext
    ) {
        this.config = config || {};
        this.apiSettings = apiSettings;
        this.productId = productId;

        if (requestContext) {
            this.catalogId = getCatalogId(requestContext);
        }
    }

    public getCacheObjectType = (): string => 'ProductPageSummary';

    public getCacheKey = (): string =>
        `${buildCacheKey('ProductPageSummary', this.apiSettings)}-${this.config.title}-${this.productId}-${this.catalogId ?? 0}`;

    public dataCacheType = (): CacheType => 'application';
}

export interface IProductPageSummary extends IPageSummaryData {
    price?: number;
    productId?: number;
    productBrand?: string;
}

const createInput = (args: ICreateActionContext) => {
    const productId = getSelectedProductIdFromActionInput(args);

    if (productId) {
        return new ProductPageSummaryInput(
            <IDobbiesProductPageSummaryConfig>args.config,
            args.requestContext.apiSettings,
            +productId,
            args.requestContext
        );
    }
    throw new Error('Unable to create ProductPageSummaryAction input, no productId found on module config or query');
};

// Checks product attributes for the presence of brand attribute to extract the brand name for product metadata
const getProductBrand = async (
    context: IActionContext,
    recordId: number,
    channelId: number,
    catalogId: number
): Promise<string | undefined> => {
    const productAttributes = await getAttributeValuesAsync(
        {
            callerContext: context,
            queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context)
        },
        recordId,
        channelId,
        catalogId
    );
    for (const productAttribute of productAttributes) {
        if (productAttribute.Name === 'Brand') {
            return productAttribute.TextValue;
        }
    }
    return;
};

const action = async (input: ProductPageSummaryInput, context: IActionContext): Promise<IProductPageSummary> => {
    const { config, productId, apiSettings } = input;
    // const jsonUrl =
    //     context.requestContext.app.config.pdpSeoJsonUrl ||
    //     'https://files-eu-prod.cms.commerce.dynamics.com/cms/api/ctdwmwnctb/binary/MD4Pas';
    // const res = await Msdyn365.sendRequest<any>(jsonUrl, 'get');
    const excelUrl =
        context.requestContext.app.config.pdpSeoTSVUrl ||
        'https://files-eu-prod.cms.commerce.dynamics.com/cms/api/ctdwmwnctb/binary/MD8qTr';
    const excelRes = await Msdyn365.sendRequest<any>(excelUrl, 'get');
    const prodID = productId.toString();
    // const pdpSeoData = res.data && res.data[prodID];

    let simpleProductsData: SimpleProduct[] | undefined;
    try {
        simpleProductsData = ArrayExtensions.validValues(
            await getByIdsAsync(
                {
                    callerContext: context,
                    queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context)
                },
                apiSettings.channelId,
                [productId],
                null,
                input.catalogId ?? 0
            )
        );
    } catch {
        // Do nothing, if there's an error we fall back to values defined from config
    }
    if (ArrayExtensions.hasElements(simpleProductsData)) {
        const simpleProductData = simpleProductsData[0];
        let productUrl: string | undefined;
        try {
            productUrl = getProductUrlSync(simpleProductData, context);
            const canonicalDomain = context.requestContext.canonicalDomain;
            if (productUrl && canonicalDomain) {
                productUrl = `https://${canonicalDomain}${productUrl}`.toLocaleLowerCase();
                productUrl = removeDomainQspFromUrl(productUrl, context.requestContext);
            } else {
                productUrl = undefined;
            }
        } catch {
            productUrl = undefined;
        }
        let productBrand: string | undefined;
        try {
            productBrand = await getProductBrand(context, productId, apiSettings.channelId, input.catalogId ?? 0);
        } catch {
            productBrand = undefined;
        }
        const excelDataStr = excelRes.data as string;
        const splittedExcelData = excelDataStr.split('\n');
        let title = simpleProductData.Name;
        let desc = stripHtmlTags(simpleProductData.Description) || simpleProductData.Name;
        let canonical = productUrl;
        for (const dataRow of splittedExcelData) {
            if (!dataRow || !dataRow.trim()) {
                continue;
            }
            const rowSplitted = dataRow.trim().split('\t');
            const curl = rowSplitted[0];
            if (prodID && (prodID === curl || curl.includes(prodID))) {
                title = rowSplitted.length > 1 ? rowSplitted[1] : simpleProductData.Name;
                desc = rowSplitted.length > 2 ? rowSplitted[2] : stripHtmlTags(simpleProductData.Description);
                canonical = rowSplitted.length > 3 ? rowSplitted[3] : productUrl;
                if (config.descriptionLimit && desc) {
                    desc = desc.substring(0, config.descriptionLimit);
                }
                return {
                    title: title,
                    description: desc,
                    sharingImageUrl: generateImageUrl(simpleProductData.PrimaryImageUrl, apiSettings),
                    canonicalUrl: canonical,
                    faviconUrl: config && config.faviconUrl,
                    productId: simpleProductData.RecordId,
                    price: simpleProductData.AdjustedPrice,
                    productBrand
                };
            }
        }
        if (config.descriptionLimit && desc) {
            desc = desc.substring(0, config.descriptionLimit);
        }
        return {
            title: title,
            description: desc,
            sharingImageUrl: generateImageUrl(simpleProductData.PrimaryImageUrl, apiSettings),
            canonicalUrl: canonical,
            faviconUrl: config && config.faviconUrl,
            productId: simpleProductData.RecordId,
            price: simpleProductData.AdjustedPrice,
            productBrand
        };
        // return {
        //     title: pdpSeoData ? pdpSeoData.title : simpleProductData.Name,
        //     description: pdpSeoData ? pdpSeoData.description : simpleProductData.Description,
        //     sharingImageUrl: generateImageUrl(simpleProductData.PrimaryImageUrl, apiSettings),
        //     canonicalUrl: pdpSeoData ? pdpSeoData.canonicalUrl : productUrl,
        //     faviconUrl: config && config.faviconUrl,
        //     productId: simpleProductData.RecordId,
        //     price: simpleProductData.AdjustedPrice,
        //     productBrand
        // };

        // If the action fails fallback to values defined from config
    } else if (config) {
        return {
            title: config.title,
            description: config.descriptionLimit ? config.description?.substring(0, config.descriptionLimit) : config.description,
            sharingImageUrl: config.sharingImage && config.sharingImage.src,
            faviconUrl: config && config.faviconUrl
        };
    }
    return {};
};

export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/page-summary/product-page-summary',
    action: <IAction<IProductPageSummary>>action,
    input: createInput
});
